import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { LinkedRecordBar, PageHeader } from '@/components/core';
import { BulkSelectionBar } from '@/components/core/bulk-selection-bar/bulk-selection-bar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui-kit/toggle-group';
import { TEAM_TIME_OFF_TABS, TeamTimeOffTab } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import {
  useLinkedRecord,
  useOpenLinkedRecord,
  useUrlChoice,
  useUrlTab,
} from '@/hooks/use-linked-record';
import { TeamToilTab } from '@/modules/time-off/team-toil';
import { toilScopeFilter } from '@/modules/time-off/team-toil/components/team-toil-tab/team-toil-tab';
import { useToilQueueCounts } from '@/modules/time-off/team-toil/hooks/use-toil-manager';
import {
  QUEUE_PARAM,
  QueueStatusFilter,
  useQueueStatus,
} from '@/components/core/queue-status-filter/queue-status-filter';
import {
  TIME_OFF_QUEUE_LABEL,
  TIME_OFF_QUEUES,
  timeOffQueueFilter,
} from '../../../shared/request-queues';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { useAuthStore } from '@/state/store/auth';
import { useDepartmentScope, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { createLeaveRequestsTableColumns } from '../../components/leave-requests-table/leave-requests-table-columns';
import { LeaveReviewDialog } from '../../components/leave-review-dialog/leave-review-dialog';
import { EMPLOYEE_PARAM, LeaveSheetTab } from '../../components/leave-sheet-tab/leave-sheet-tab';
import { LeaveOverviewTab } from '../../components/leave-overview-tab/leave-overview-tab';
import {
  useBulkReviewLeaveRequests,
  useGetLeaveRequests,
  leaveScopeFilter,
  useLeaveQueueCounts,
} from '../../hooks/use-leave-manager';
import { DEFAULT_LEAVE_REQUESTS_ORDER } from '../../graphql/queries';
import {
  LeaveRequest,
  LeaveRequestsPagingResult,
  LeaveStatus,
} from '../../types/leave-manager.types';


const RequestsTab = ({
  queueFilter,
  queueSwitch,
}: {
  /** The open queue's `where` (status / archived). */
  queueFilter: Record<string, unknown>;
  /** The queue switch, shown in the table toolbar until rows are selected. */
  queueSwitch: ReactNode;
}) => {
  const { t } = useTranslation();
  const canReview = useHasPermission(HRM_PERMISSIONS.TIME_OFF.LEAVE_REVIEW);
  const currentUserId = useAuthStore((state) => state.user?.itemId ?? '');

  // A notification's link lists only its request and opens it.
  const { linkedId, clearLinked } = useLinkedRecord();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_LEAVE_REQUESTS_ORDER,
    linkedId,
  });
  const [reviewTarget, setReviewTarget] = useState<LeaveRequest | null>(null);

  const { departments, getDepartmentIdCondition, isLoading: isScopeLoading } = useDepartmentScope();

  // The department scope (the Department permissions, as the TOIL Manager) always applies, so a
  // reviewer limited to some departments never sees requests outside them; the Department column's
  // filter only narrows within it (mergeWhere). The leave workflows still check who may act.
  const departmentCondition = getDepartmentIdCondition('');
  const filter = { ...queueFilter, ...leaveScopeFilter(departmentCondition) };

  const { data, isLoading, error } = useGetLeaveRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { LeaveRequests?: LeaveRequestsPagingResult })?.LeaveRequests;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;
  useOpenLinkedRecord(linkedId, items, setReviewTarget);

  const { byUserId } = useEmployeesByUserIds(items.map((item) => item.UserId));
  const emailOf = (userId: string) => byUserId.get(userId)?.Email;

  // No action column: clicking a row (a card on a phone) opens the request's details.
  const columns = createLeaveRequestsTableColumns({
    t,
    selectable: canReview,
    emailOf,
    departments,
  });

  // Bulk approve/reject still runs the per-item LEAVE_REQUEST_REVIEW workflow call (useBulkReviewLeaveRequests
  // → bulkReviewLeaveRequests → the same reviewLeaveRequest the single-row dialog uses), so every item is
  // independently re-checked server-side (permission, self-review block, Pending status) — no new write path.
  const {
    mutateAsync: bulkReview,
    isPending: isBulkReviewing,
    variables: bulkReviewVariables,
  } = useBulkReviewLeaveRequests();
  // Only the clicked button spins; the others are just disabled while it runs.
  const bulkReviewingStatus = isBulkReviewing ? bulkReviewVariables?.status : undefined;

  // A row can be bulk-selected only when it's Pending and isn't the signed-in user's own request — the
  // same client-side mirror of the workflow's self-review block LeaveReviewDialog uses, applied up front
  // so the row's checkbox is disabled instead of letting it be picked and then refused one by one.
  const canSelectRow = useCallback(
    (row: LeaveRequest) =>
      canReview && row.Status === 'Pending' && (!currentUserId || row.UserId !== currentUserId),
    [canReview, currentUserId]
  );

  return (
    <div className="flex flex-col gap-4">
      <LinkedRecordBar linkedId={linkedId} onShowAll={clearLinked} />
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        getRowId={(row) => row.ItemId}
        canSelectRow={canSelectRow}
        clearSelectionOnPageChange
        // Until the departments load, a scoped user's scope matches nothing; show the loader, not an
        // empty list.
        isLoading={isLoading || isScopeLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        onRowClick={setReviewTarget}
        mobileView="cards"
        columnsToolbar={(table) => {
          const selectedRows = table.getSelectedRowModel().rows;
          // The toolbar holds the queue switch; while rows are selected it becomes the bulk bar.
          if (!canReview || selectedRows.length === 0) return queueSwitch;
          const itemIds = selectedRows.map((row) => row.original.ItemId);

          const runBulkReview = async (status: Extract<LeaveStatus, 'Approved' | 'Rejected'>) => {
            await bulkReview({ itemIds, status });
            table.resetRowSelection();
          };

          return (
            <BulkSelectionBar
              table={table}
              count={itemIds.length}
              onApprove={() => runBulkReview('Approved')}
              onReject={() => runBulkReview('Rejected')}
              running={
                bulkReviewingStatus === 'Approved'
                  ? 'approve'
                  : bulkReviewingStatus === 'Rejected'
                    ? 'reject'
                    : undefined
              }
            />
          );
        }}
      />

      <LeaveReviewDialog
        open={Boolean(reviewTarget)}
        onOpenChange={(open) => !open && setReviewTarget(null)}
        leaveRequest={reviewTarget}
      />
    </div>
  );
};

const { OVERVIEW, REQUESTS, TOIL } = TEAM_TIME_OFF_TABS;
/** Tab names of the former Leave Manager page, still in links already sent. */
const TAB_ALIASES: Record<string, TeamTimeOffTab> = {
  all: REQUESTS,
  archived: REQUESTS,
  'leave-sheet': OVERVIEW,
  'leave-overview': OVERVIEW,
};
/** The Overview tab shows the leave overview table or one employee's leave sheet (`?view=sheet`). */
const VIEW_PARAM = 'view';
const VIEWS = ['overview', 'sheet'] as const;

const OverviewTab = () => {
  const { t } = useTranslation();
  const [view, setView] = useUrlChoice(VIEW_PARAM, VIEWS, 'overview', {
    aliases: { 'leave-sheet': 'sheet' },
  });
  const [, setParams] = useSearchParams();

  return (
    <div className="flex flex-col gap-4">
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        className="self-start"
        value={view}
        onValueChange={(value) => value && setView(value)}
        aria-label={t('OVERVIEW')}
      >
        <ToggleGroupItem value="overview">{t('LEAVE_OVERVIEW')}</ToggleGroupItem>
        <ToggleGroupItem value="sheet">{t('LEAVE_SHEET')}</ToggleGroupItem>
      </ToggleGroup>
      {view === 'sheet' ? (
        <LeaveSheetTab />
      ) : (
        <LeaveOverviewTab
          onOpenSheet={(userId) =>
            setParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set(VIEW_PARAM, 'sheet');
              next.set(EMPLOYEE_PARAM, userId);
              return next;
            })
          }
        />
      )}
    </div>
  );
};

const LeaveRequestsTab = () => {
  // Pending first; a notification's link opens All, so a decided request still shows.
  const [queue, setQueue] = useQueueStatus(TIME_OFF_QUEUES, 'Pending', 'All');
  const { getDepartmentIdCondition } = useDepartmentScope();
  const counts = useLeaveQueueCounts(leaveScopeFilter(getDepartmentIdCondition('')));
  return (
    // A fresh list (first page, its own filters) for each queue.
    <RequestsTab
      key={queue}
      queueFilter={timeOffQueueFilter(queue)}
      queueSwitch={
        <QueueStatusFilter
          value={queue}
          onChange={setQueue}
          options={TIME_OFF_QUEUES.map((value) => ({
            value,
            label: TIME_OFF_QUEUE_LABEL[value],
            count: counts[value],
          }))}
        />
      }
    />
  );
};

/**
 * Team Time Off (HR Module Navigation Map): Overview (the leave overview and leave sheets of the team),
 * Leave Requests and TOIL Requests (the former TOIL Manager), each by queue (Pending, Approved,
 * Rejected, Archived, All). It opens on Leave Requests, as the prototype does; the two request tabs
 * show how many requests wait. It has no Request Time Off / Request TOIL buttons (agreed 2026-11-02:
 * a team page is for reviewing). The tab lives in
 * the URL (`?tab=`).
 */
export const TeamTimeOffPage = () => {
  const { t } = useTranslation();
  const canViewLeave = useHasPermission(HRM_PERMISSIONS.TIME_OFF.LEAVE_VIEW_ALL);
  const canViewToil = useHasPermission(HRM_PERMISSIONS.TIME_OFF.TOIL_VIEW_ALL);

  const tabs: TeamTimeOffTab[] = [
    ...(canViewLeave ? [OVERVIEW, REQUESTS] : []),
    ...(canViewToil ? [TOIL] : []),
  ];
  const fallback = canViewLeave ? REQUESTS : (tabs[0] ?? REQUESTS);
  const [tab, setTab] = useUrlTab(tabs, fallback, TAB_ALIASES, [QUEUE_PARAM, VIEW_PARAM]);
  // Requests waiting for review, on their tabs (the same queries as the tabs' Pending queues).
  const { getDepartmentIdCondition } = useDepartmentScope();
  const scope = getDepartmentIdCondition('');
  const pendingLeave = useLeaveQueueCounts(leaveScopeFilter(scope), canViewLeave).Pending;
  const pendingToil = useToilQueueCounts(toilScopeFilter(scope), canViewToil).Pending;

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('TEAM_TIME_OFF')} />

      <Tabs value={tab} onValueChange={setTab}>
        {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
        <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
          {canViewLeave && (
            <>
              <TabsTrigger value={OVERVIEW} className="max-sm:flex-1">
                {t('OVERVIEW')}
              </TabsTrigger>
              <TabsTrigger value={REQUESTS} className="max-sm:flex-1" count={pendingLeave}>
                {t('LEAVE_REQUESTS')}
              </TabsTrigger>
            </>
          )}
          {canViewToil && (
            <TabsTrigger value={TOIL} className="max-sm:flex-1" count={pendingToil}>
              {t('TOIL_REQUESTS')}
            </TabsTrigger>
          )}
        </TabsList>
        {canViewLeave && (
          <>
            <TabsContent value={OVERVIEW}>
              <OverviewTab />
            </TabsContent>
            <TabsContent value={REQUESTS}>
              <LeaveRequestsTab />
            </TabsContent>
          </>
        )}
        {canViewToil && (
          <TabsContent value={TOIL}>
            <TeamToilTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
